'use client';

import { useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { useRef } from 'react';

import { RingProSelectionContextProvider } from 'components/ring-pro/RingProSelectionContext';
import { RingProProductSelectionSection } from 'components/ring-pro/ProductSelectionSection';
import { RingProBottomBar } from 'components/ring-pro/BottomBar';
import { RingProContents } from 'components/ring-pro/RingProContents';
import { BgBlurredTextRingPro } from 'components/ring-pro/BgBlurredTextRingPro';
import {
  AppSection,
  // ChargerFeaturesSection,
  // ChargerIntroSection,
  ComparisonSection,
  DesignSection,
  EngineeringSection,
  // ImageSection,
  JadeAISection,
  UltraSnapSection,
  VideoSection,
} from 'components/ring-pro';
import { ProductWizard } from 'components/shared/ProductWizard';
import { FAQSection } from 'components/shared/FAQSection';
// import { TechnicalSpecs } from 'components/technicalSpecs/TechnicalSpecs';
// import { getRingProTechnicalSpecsConfig } from 'components/technicalSpecs/technicalSpecsConfigs';
import { JsonLd } from 'components/shared';
import {
  ClaimSizingKitPopup,
  ClaimSizingKitPopupHandle,
} from 'components/shared/ClaimSizingKitPopup';
import {
  RING_PRO_TYPEFORM_ID,
  shouldShowSizingKitPopup,
} from 'components/shared/ClaimSizingKitPopup/constants';
import { getRingProPricingSchemas } from 'lib/seo/meta/pricing/ringPro';
// import { useGlobalUI } from 'contexts/GlobalUIContext';
import { Currency } from 'utils/Currency';
import { RingProColors, RingSizes } from 'content/products/ring-pro';
import { getRingProBuyPageFaqData } from 'content/products/ring/faqData';
import { ringProKeyT } from 'content/products/ring-pro/ringProKeyT';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { getOffersForRegion } from 'components/ring-air/utils/offersByRegion';
import type {
  ShippingEstimateObject,
  ServerDiscountInfo,
  TierCampaignData,
} from 'components/ring-pro/utils/types';
import { getRingProBasePrice } from 'content/products/ring-pro/tierPricing';
import { useTripleWhaleProductView } from 'hooks/analytics/useTripleWhaleProductView';
import styles from './ring-pro-buy-client.module.css';

export type PreorderAccess = 'uhx_only' | 'all';

type RingProBuyPageProps = {
  region: string;
  language: string;
  defaultSize: RingSizes | null;
  defaultColor: RingProColors | null;
  defaultUhx: boolean;
  estimates?: ShippingEstimateObject | null;
  store: ShopifyStore;
  currency: Currency;
  serverDiscount: ServerDiscountInfo;
  preorderAccess: PreorderAccess;
  isRingProAvailableInUS: boolean;
  tierCampaign: TierCampaignData | null;
};

export default function RingProBuyPage(props: RingProBuyPageProps) {
  const t = useTranslations('ring');
  useTripleWhaleProductView('ring-pro', props.store, props.currency);
  const tRingPro = useTranslations('ring-pro');
  const router = useRouter();
  const searchParams = useSearchParams();
  const { regionSlug } = useRegionLocale();
  const sizingKitRef = useRef<ClaimSizingKitPopupHandle>(null);
  const showSizingKitPopup = shouldShowSizingKitPopup(props.region, 'ring_pro');
  // useGlobalUI({ headerCollaspable: true });

  const { offersData, secondaryOfferData } = getOffersForRegion(props.region, {
    t,
    regionSlug,
    router: {
      push: (url: string) => router.push(url),
      query: Object.fromEntries(searchParams?.entries() ?? []),
    },
    product: 'ring-pro',
  });

  const notServicable =
    (props.region.toUpperCase() === 'US' ||
      props.region.toUpperCase() === 'PR') &&
    !props.isRingProAvailableInUS;

  const showUhxFirstOnly =
    props.preorderAccess === 'uhx_only' && !props.defaultUhx;

  const showBannerAndGrey = notServicable || showUhxFirstOnly;
  // Unavailable in US takes precedence over UHX-only: show US message when both apply
  const notServicableMessageToUse = notServicable
    ? t('ring.buyPage.notServicableMessage')
    : showUhxFirstOnly
      ? tRingPro('ring-pro.buyPage.uhxFirstMessage')
      : undefined;
  const showUhxFirstBanner = showUhxFirstOnly && !notServicable;

  const basePriceForRegion =
    getRingProBasePrice(props.store, props.currency) ?? undefined;
  const resolvedPrice = props.serverDiscount?.applicable
    ? (props.serverDiscount.discountedPrice ?? basePriceForRegion)
    : basePriceForRegion;

  const ringProSchemas = getRingProPricingSchemas({
    locale: props.region.toLowerCase(),
    country: props.region.toLowerCase(),
    regionSlug,
    store: props.store,
    currency: props.currency,
    price: resolvedPrice,
    t: ringProKeyT(t, tRingPro),
  });

  return (
    <>
      <JsonLd data={ringProSchemas} />
      <RingProSelectionContextProvider
        currency={props.currency}
        defaultSize={props.defaultSize}
        defaultColor={props.defaultColor ?? RingProColors.GOLD}
        defaultUhx={props.defaultUhx}
        serverDiscount={props.serverDiscount}
        estimates={props.estimates ?? undefined}
        notServicable={showBannerAndGrey}
        notServicableMessage={notServicableMessageToUse}
        uhxFirstOnly={showUhxFirstBanner}
        showEngravingSectionProp={false}
        tierCampaign={props.tierCampaign}
        onCheckoutStarted={() => sizingKitRef.current?.cancelPopup()}
      >
        {/* Main landmark (see .main); the sticky bottom bar stays outside. */}
        <main className={styles.main}>
          <RingProProductSelectionSection
            description={tRingPro('ring-pro.productSelection.description')}
            offersData={offersData}
            secondaryOfferData={secondaryOfferData}
          />
          <div
            style={{ filter: showBannerAndGrey ? 'grayscale(1)' : undefined }}
          >
            <RingProContents />
            <BgBlurredTextRingPro />
            {/* <TechnicalSpecs htmlId="ring-pro-specs" config={getRingProTechnicalSpecsConfig(t)} disableMobileAccordion /> */}

            <UltraSnapSection skipChargerWait />
            <JadeAISection />
            <DesignSection />
            <EngineeringSection />
            <VideoSection />
            <AppSection />
            {/* <ImageSection /> */}
            <ComparisonSection htmlId="pro-vs-air" />
            <ProductWizard title={t('ring.ring-pro.productWizard.contentA')} />
            <FAQSection
              faqData={getRingProBuyPageFaqData(ringProKeyT(t, tRingPro))}
              htmlId="ring-pro-faq"
            />
          </div>
        </main>
        <RingProBottomBar />
        {showSizingKitPopup && (
          <ClaimSizingKitPopup
            ref={sizingKitRef}
            region={props.region}
            product="ring_pro"
            typeformId={RING_PRO_TYPEFORM_ID}
          />
        )}
      </RingProSelectionContextProvider>
    </>
  );
}
